import React from 'react'

import {
  Button,
  Truncate,
  Table,
  Tabs,
  TabPanel,
  Tab,
  TabList,
  TableComponents,
  TimeAgo,
  ShowModal,
  useModalContext,
} from '@sketch/components'

import {
  NameAndExpiryContainer,
  TableWrapper,
  TokenName,
  TokenExpiry,
} from './ScimPersonalAccessTokenList.styles'

import TokenDetailsModal from '../../modals/ScimPersonalAccessTokenDetailsModal'
import DeleteTokenConfirmationModal from '../../modals/DeleteScimPersonalAccessTokenConfirmationModal'

import { ScimPersonalAccessTokenFragment } from '@sketch/gql-types'

import { dateFormat } from '@sketch/utils'

const localizeDate = (dateStr: string): string => dateFormat(new Date(dateStr))

interface TokenTableProps {
  showModal: ShowModal
  tokens: ScimPersonalAccessTokenFragment[]
}

const tableHeader = [
  { label: 'Name' },
  { label: 'Last Used At' },
  { label: 'Actions' },
]

const TokenTable: React.FC<TokenTableProps> = ({ tokens, showModal }) => {
  const handleDeleteClick = (token: ScimPersonalAccessTokenFragment) => {
    showModal(DeleteTokenConfirmationModal, { token })
  }

  const handleRowClick = (token: ScimPersonalAccessTokenFragment) => {
    showModal(TokenDetailsModal, { token })
  }

  const tokenRow = (token: ScimPersonalAccessTokenFragment) => {
    const now = new Date()
    const expired = !!token.expiresAt && new Date(token.expiresAt) <= now

    const expiresAtPrefix = expired ? 'Expired on' : 'Valid until'
    const expiresAt = token.expiresAt
      ? `${expiresAtPrefix} ${localizeDate(token.expiresAt)}`
      : ''

    const lastUsedAt = token.lastUsedAt ? (
      <>
        Used <TimeAgo date={token.lastUsedAt} />
      </>
    ) : (
      'Unused'
    )

    return (
      <TableComponents.TableRow
        data-testid={`token-row-${token.identifier}`}
        onClick={() => handleRowClick(token)}
        key={token.identifier}
      >
        <TableComponents.TableCell>
          <NameAndExpiryContainer>
            <TokenName expired={expired}>
              <Truncate>{token.description}</Truncate>
            </TokenName>
            <TokenExpiry>{expiresAt}</TokenExpiry>
          </NameAndExpiryContainer>
        </TableComponents.TableCell>
        <TableComponents.TableCell>{lastUsedAt}</TableComponents.TableCell>
        <TableComponents.TableCell>
          <Button
            variant="secondary-untinted"
            size="32"
            onClick={e => {
              e.stopPropagation()
              handleDeleteClick(token)
            }}
          >
            Delete…
          </Button>
        </TableComponents.TableCell>
      </TableComponents.TableRow>
    )
  }

  return (
    <TableWrapper>
      <Table
        header={tableHeader}
        items={tokens}
        renderItem={tokenRow}
        hideHeader
      />
    </TableWrapper>
  )
}

const dateDescOrder = (
  token1: ScimPersonalAccessTokenFragment,
  token2: ScimPersonalAccessTokenFragment
): number => {
  const date1: Date = new Date(token1.createdAt)
  const date2: Date = new Date(token2.createdAt)
  return date2.getTime() - date1.getTime()
}

interface ScimPersonalAccessTokenListProps {
  tokens: ScimPersonalAccessTokenFragment[]
}

const ScimPersonalAccessTokenList: React.FC<
  ScimPersonalAccessTokenListProps
> = ({ tokens }) => {
  const now = new Date()

  // We show tokens ordered by their createdAt date, in descending order.
  // And all "active" tokens should appear before "expired" tokens.
  const sortedTokens = tokens.sort(dateDescOrder)
  const activeTokens = sortedTokens.filter(
    ({ expiresAt }) => expiresAt === null || new Date(expiresAt) > now
  )
  const expiredTokens = sortedTokens.filter(
    ({ expiresAt }) => expiresAt !== null && new Date(expiresAt) <= now
  )
  const allTokens = [...activeTokens, ...expiredTokens]

  const { showModal } = useModalContext()

  return (
    <Tabs>
      <TabList>
        <Tab>{tokens.length} Total</Tab>
        <Tab>{activeTokens.length} Active</Tab>
        <Tab>{expiredTokens.length} Expired</Tab>
      </TabList>
      <TabPanel>
        <TokenTable tokens={allTokens} showModal={showModal} />
      </TabPanel>
      <TabPanel>
        <TokenTable tokens={activeTokens} showModal={showModal} />
      </TabPanel>
      <TabPanel>
        <TokenTable tokens={expiredTokens} showModal={showModal} />
      </TabPanel>
    </Tabs>
  )
}

export default ScimPersonalAccessTokenList
